import * as React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { RoastData } from '../lib/analyzerService';
import PromoBanner from '../components/PromoBanner';
import RoastCountdown from '../components/RoastCountdown';
import TestimonialSection from '../components/TestimonialSection';
import Footer from '../components/Footer';
import { ShareButtons } from '../components/ShareButtons';
import '../styles/ViralComponent.css';
import { FaThumbsUp, FaRegThumbsUp, FaThumbsDown, FaRegThumbsDown, FaShareAlt, FaFire } from 'react-icons/fa';
import { getEngagement, getUserVote, recordVote, recordShare } from '../lib/engagementService';
import { env } from '../env';
import { logger } from '../lib/loggerService';

// Get the Amazon affiliate tag from environment variables
const AMAZON_AFFILIATE_TAG = env.AMAZON_AFFILIATE_TAG;

// Function to get the correct Amazon domain based on marketplace
const getAmazonDomain = (marketplace: string = 'com'): string => {
  const domains: {[key: string]: string} = {
    'com': 'amazon.com',
    'co.uk': 'amazon.co.uk',
    'ca': 'amazon.ca',
    'com.au': 'amazon.com.au'
  };
  return domains[marketplace] || 'amazon.com'; // Default to US site if marketplace not found
};

// Function to auto-detect API port
const detectApiPort = async (): Promise<string> => {
  // In production mode, just use the configured API_BASE_URL to avoid console errors
  if (!env.DEBUG_MODE) {
    return env.API_BASE_URL;
  }
  
  const potentialPorts = [5004, 5003, 5002, 5001, 5000];
  const apiHost = 'localhost';
  const defaultPort = 5004; // Default to the configured port
  
  // Use Promise.allSettled to try all ports in parallel without showing errors
  const portChecks = potentialPorts.map(port => {
    return new Promise<string | null>(async (resolve) => {
      try {
        const url = `http://${apiHost}:${port}/health`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300); // Shorter timeout
        
        const response = await fetch(url, { 
          method: 'GET',
          signal: controller.signal 
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          logger.log(`Found API server running on port ${port}`);
          resolve(`http://${apiHost}:${port}`);
        } else {
          resolve(null);
        }
      } catch (error) {
        // Silently handle the error and resolve with null
        resolve(null);
      }
    });
  });
  
  try {
    const results = await Promise.allSettled(portChecks);
    
    // Find the first successful port
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) {
        return result.value;
      }
    }
    
    // If no port is available, return the default
    logger.log(`No API server found on common ports, defaulting to configured base URL`);
    return env.API_BASE_URL;
  } catch (error) {
    // In case of any errors, fallback to the default
    return env.API_BASE_URL;
  }
};

const Results: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [data, setData] = React.useState<RoastData | null>(null);
  const [marketplace, setMarketplace] = React.useState<string>('com'); // Default to US marketplace
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [showCountdown, setShowCountdown] = React.useState(false);
  const [createdAt, setCreatedAt] = React.useState<string>('');
  const [apiBaseUrl, setApiBaseUrl] = React.useState<string>(env.API_BASE_URL);
  const navigate = useNavigate();
  const [isDirectAccess, setIsDirectAccess] = React.useState(!!shareId);

  const loadStoredRoastForShareId = React.useCallback((): RoastData | null => {
    if (!shareId) {
      return null;
    }

    try {
      const storedData = localStorage.getItem('lastRoastResult');
      if (!storedData) {
        return null;
      }

      const parsedData = JSON.parse(storedData);
      if (parsedData?.asin === shareId || parsedData?.share_id === shareId) {
        logger.log(`Using localStorage fallback for shareId: ${shareId}`);
        return parsedData;
      }
    } catch (error) {
      logger.error('Error reading local roast fallback:', error);
    }

    return null;
  }, [shareId]);

  // Use window width for responsive layout
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  // Detect API port when component mounts
  React.useEffect(() => {
    const detectPort = async () => {
      try {
        const detectedBaseUrl = await detectApiPort();
        if (detectedBaseUrl !== apiBaseUrl) {
          logger.log(`Updating API base URL to: ${detectedBaseUrl}`);
          setApiBaseUrl(detectedBaseUrl);
        }
      } catch (error) {
        logger.error('Error detecting API port:', error);
      }
    };
    
    detectPort();
  }, []);

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Destructure data for easier access with defaults for null or undefined values
  const {
    asin = '',
    product_name = '',
    product_image = '',
    price = '',
    rating = 0,
    review_count = 0,
    listing_screenshot = '',
    analysis = {
      roast_level: 'medium',
      roast_text: '',
      score: 0,
      strengths: [],
      weaknesses: [],
      metrics: {
        title_score: 0,
        images_score: 0,
        description_score: 0,
        price_score: 0,
        bullet_points_score: 0,
        reviews_score: 0
      },
      priority_actions: [],
      competitive_insight: '',
      compliance_alerts: []
    }
  } = data || {};

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (shareId) {
          try {
            // Check if the shareId looks like an ASIN (typically 10 characters starting with B)
            const isASIN = /^B0[0-9A-Z]{8}$/.test(shareId);
            const storedFallback = loadStoredRoastForShareId();
            
            // Use the appropriate endpoint based on whether shareId is actually an ASIN
            const endpoint = isASIN ? `${apiBaseUrl}/api/roast/${shareId}` : `${apiBaseUrl}/api/share/${shareId}`;
            
            const response = await fetch(endpoint);
            
            if (response.ok) {
              const apiData = await response.json();
              logger.log(`Received roast data by shareId: ${shareId}`, JSON.stringify(apiData, null, 2));
              logger.log("DETAILED DATA INSPECTION:");
              logger.log("Priority actions:", apiData.analysis?.priority_actions);
              logger.log("Type of priority actions:", typeof apiData.analysis?.priority_actions);
              logger.log("Is array:", Array.isArray(apiData.analysis?.priority_actions));
              
              // If it's a string, try to parse it
              if (typeof apiData.analysis?.priority_actions === 'string') {
                try {
                  const parsedActions = JSON.parse(apiData.analysis.priority_actions);
                  logger.log("Successfully parsed priority_actions from string:", parsedActions);
                  apiData.analysis.priority_actions = parsedActions;
                } catch (e) {
                  logger.error("Error parsing priority_actions string:", e);
                }
              }
              
              logger.log("Analysis improvements:", apiData.analysis?.improvements);
              logger.log("Top-level improvements:", apiData.improvements);
              
              setData(apiData);
              setCreatedAt(apiData.created_at || '');
              
              // Update marketplace if available in the data
              if (apiData.marketplace) {
                setMarketplace(apiData.marketplace);
              }
              
              // For direct access, don't show countdown, just load the data
              if (isDirectAccess) {
                setShowCountdown(false);
              }
            } else {
              if (storedFallback) {
                logger.log(`API returned ${response.status}; using localStorage fallback for ${shareId}`);
                setData(storedFallback);
                setCreatedAt(storedFallback.created_at || '');
                if (storedFallback.marketplace) {
                  setMarketplace(storedFallback.marketplace);
                }
                setShowCountdown(false);
              } else {
                setError('Failed to load roast data. Please try searching for the product again.');
              }
            }
          } catch (error) {
            logger.error('Error fetching roast data:', error);
            const storedFallback = loadStoredRoastForShareId();
            if (storedFallback) {
              logger.log(`Fetch failed; using localStorage fallback for ${shareId}`);
              setData(storedFallback);
              setCreatedAt(storedFallback.created_at || '');
              if (storedFallback.marketplace) {
                setMarketplace(storedFallback.marketplace);
              }
              setShowCountdown(false);
            } else {
              setError('Failed to load roast data. Please try searching for the product again.');
            }
          }
        } else {
          // Handle case when coming from search
          const storedData = localStorage.getItem('lastRoastResult');
          if (storedData) {
            const parsedData = JSON.parse(storedData);
            logger.log(`Received stored roast data by ASIN: ${parsedData.asin}`, JSON.stringify(parsedData, null, 2));
            logger.log("DETAILED DATA INSPECTION from localStorage:");
            logger.log("Priority actions:", parsedData.analysis?.priority_actions);
            logger.log("Type of priority actions:", typeof parsedData.analysis?.priority_actions);
            logger.log("Is array:", Array.isArray(parsedData.analysis?.priority_actions));
            
            // If it's a string, try to parse it
            if (typeof parsedData.analysis?.priority_actions === 'string') {
              try {
                const parsedActions = JSON.parse(parsedData.analysis.priority_actions);
                logger.log("Successfully parsed priority_actions from string:", parsedActions);
                parsedData.analysis.priority_actions = parsedActions;
              } catch (e) {
                logger.error("Error parsing priority_actions string:", e);
              }
            }
            
            logger.log("Analysis improvements:", parsedData.analysis?.improvements);
            logger.log("Top-level improvements:", parsedData.improvements);
            
            setData(parsedData);
            setCreatedAt(parsedData.created_at || '');
            
            // Update marketplace if available in localStorage data
            if (parsedData.marketplace) {
              setMarketplace(parsedData.marketplace);
            }
            
            // Only show countdown if we're not accessing directly via URL
            // and if the data is older than a certain threshold
            const creationDate = new Date(parsedData.created_at || Date.now());
            const now = new Date();
            const hoursSinceCreation = (now.getTime() - creationDate.getTime()) / (1000 * 60 * 60);
            
            // Only show countdown if data is older than 24 hours but less than 72
            if (hoursSinceCreation > 24 && hoursSinceCreation < 72) {
              setShowCountdown(true);
            }
          } else {
            setError('No roast data found. Please try analyzing a product first.');
          }
        }
      } catch (error) {
        logger.error('Error in data fetching:', error);
        setError('An error occurred while loading the data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [shareId, isDirectAccess, apiBaseUrl, loadStoredRoastForShareId]);

  // Helper function to format database response
  const formatDatabaseResponseToRoastData = (dbData: any) => {
    logger.log('Raw database data:', dbData);
    
    // Parse improvements if it's a string
    let improvements = dbData.improvements;
    if (typeof improvements === 'string') {
      try {
        improvements = JSON.parse(improvements);
      } catch (e) {
        logger.error('Error parsing improvements:', e);
        improvements = [];
      }
    }
    
    // Parse strengths if it's a string
    let strengths = dbData.strengths;
    if (typeof strengths === 'string') {
      try {
        strengths = JSON.parse(strengths);
        logger.log('Parsed strengths:', strengths);
      } catch (e) {
        logger.error('Error parsing strengths:', e);
        strengths = [];
      }
    }
    
    // Parse weaknesses if it's a string
    let weaknesses = dbData.weaknesses;
    if (typeof weaknesses === 'string') {
      try {
        weaknesses = JSON.parse(weaknesses);
        logger.log('Parsed weaknesses:', weaknesses);
      } catch (e) {
        logger.error('Error parsing weaknesses:', e);
        weaknesses = [];
      }
    }
    
    // Parse compliance_alerts if it's a string
    let compliance_alerts = dbData.compliance_alerts;
    if (typeof compliance_alerts === 'string') {
      try {
        compliance_alerts = JSON.parse(compliance_alerts);
        logger.log('Parsed compliance_alerts:', compliance_alerts);
      } catch (e) {
        logger.error('Error parsing compliance_alerts:', e);
        compliance_alerts = [];
      }
    }
    
    // Create a properly formatted RoastData object
    const formattedData = {
      asin: dbData.asin,
      product_name: dbData.product_name,
      product_image: dbData.product_image,
      listing_screenshot: dbData.listing_screenshot,
      price: dbData.price,
      rating: dbData.rating,
      review_count: dbData.review_count,
      analysis: {
        roast_level: dbData.roast_level || 'medium',
        roast_text: dbData.roast_text,
        score: dbData.score,
        strengths: strengths || [],
        weaknesses: weaknesses || [],
        metrics: dbData.metrics || {
          title_score: 0,
          images_score: 0,
          description_score: 0,
          price_score: 0,
          bullet_points_score: 0,
          reviews_score: 0
        },
        priority_actions: improvements || [],
        competitive_insight: dbData.competitive_insight || '',
        compliance_alerts: compliance_alerts || []
      }
    };
    
    logger.log('Formatted data:', formattedData);
    return formattedData;
  };

  // Close countdown modal
  const handleCloseCountdown = () => {
    setShowCountdown(false);
  };

  // Load anyway from countdown modal
  const handleLoadAnyway = () => {
    // This function will be called when the user clicks "Load Anyway" in the countdown
    setShowCountdown(false);
    
    // Make sure we have the data to display
    if (!data && shareId) {
      // If we don't have data yet, try to fetch it from localStorage
      const storedData = localStorage.getItem('lastRoastResult');
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setData(parsedData);
      }
    }
  };

  // Initialize engagement metrics with random values if not present
  const [likes, setLikes] = React.useState(0);
  const [dislikes, setDislikes] = React.useState(0);
  const [shares, setShares] = React.useState(0);
  const [userVote, setUserVote] = React.useState<'like' | 'dislike' | null>(null);
  const [copySuccess, setCopySuccess] = React.useState(false);
  const [notification, setNotification] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchEngagement = async () => {
      if (data && asin) {
        try {
          const engagement = await getEngagement(asin);
          setLikes(engagement.likes);
          setDislikes(engagement.dislikes);
          setShares(engagement.shares);
        } catch (error) {
          logger.error('Error fetching engagement:', error);
        }
      }
    };

    fetchEngagement();
  }, [data, asin]);

  React.useEffect(() => {
    const fetchUserVote = async () => {
      if (data && asin) {
        try {
          const userVote = await getUserVote(asin);
          setUserVote(userVote);
        } catch (error) {
          logger.error('Error fetching user vote:', error);
        }
      }
    };

    fetchUserVote();
  }, [data, asin]);

  const handleVote = async (vote: 'like' | 'dislike' | null) => {
    if (data && asin) {
      try {
        // Only proceed if the vote is different from current userVote
        if (vote !== userVote) {
          await recordVote(asin, vote);
          
          // Decrement previous vote if it exists
          if (userVote === 'like') {
            setLikes((prev: number) => prev - 1);
          } else if (userVote === 'dislike') {
            setDislikes((prev: number) => prev - 1);
          }
          
          // Increment new vote
          if (vote === 'like') {
            setLikes((prev: number) => prev + 1);
          } else if (vote === 'dislike') {
            setDislikes((prev: number) => prev + 1);
          }
          
          setUserVote(vote);
        }
      } catch (error) {
        logger.error('Error recording vote:', error);
      }
    }
  };

  const handleShare = (platform: string, shareText: string) => {
    // Then handle the share action
    const productUrl = `${window.location.origin}/results/${asin}`;
    const fullShareText = shareText + '\n' + productUrl;
    
    // Common window features for popup
    const popupWidth = 600;
    const popupHeight = 400;
    const left = window.innerWidth / 2 - popupWidth / 2;
    const top = window.innerHeight / 2 - popupHeight / 2;
    const windowFeatures = `width=${popupWidth},height=${popupHeight},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,location=no,status=no`;
    
    // Copy text to clipboard for Facebook and LinkedIn
    const copyTextForPlatform = async (platformName: string) => {
      try {
        await navigator.clipboard.writeText(fullShareText);
        setNotification(`Text copied to clipboard! Paste it in the ${platformName} window that opened.`);
        setTimeout(() => setNotification(null), 5000);
      } catch (err) {
        logger.error('Failed to copy:', err);
      }
    };
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(productUrl)}`, 'twittershare', windowFeatures);
        break;
      case 'facebook':
        // First copy text to clipboard
        copyTextForPlatform('Facebook');
        // Then open Facebook in popup
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`, 'facebookshare', windowFeatures);
        break;
      case 'linkedin':
        // First copy text to clipboard
        copyTextForPlatform('LinkedIn');
        // Then open LinkedIn in popup
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(productUrl)}`, 'linkedinshare', windowFeatures);
        break;
      case 'whatsapp':
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + ' ' + productUrl)}`, 'whatsappshare', windowFeatures);
        break;
      case 'copy':
        // For copy, just copy the URL
        navigator.clipboard.writeText(productUrl).then(() => {
          setCopySuccess(true);
          setNotification('Link copied!');
          setTimeout(() => {
            setCopySuccess(false);
            setNotification(null);
          }, 3000);
        });
        break;
    }
    
    // Record the share
    recordShare(asin ?? '', platform).then(engagement => {
      setShares(engagement.shares);
    }).catch(error => {
      logger.error('Error recording share:', error);
    });
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Ensure data from localStorage is properly formatted
  React.useEffect(() => {
    if (data) {
      // Make sure all required array properties exist to prevent map() errors
      if (!data.analysis) {
        setData(prev => {
          if (!prev) return null;
          return {
            ...prev,
            asin: prev.asin,
            product_name: prev.product_name || '',
            product_image: prev.product_image || '',
            analysis: {
              roast_level: 'medium',
              roast_text: '',
              score: 0,
              strengths: [],
              weaknesses: [],
              metrics: {
                title_score: 0,
                images_score: 0,
                description_score: 0,
                price_score: 0,
                bullet_points_score: 0,
                reviews_score: 0
              },
              priority_actions: [],
              competitive_insight: '',
              compliance_alerts: []
            }
          } as RoastData;
        });
        return;
      }

      const { analysis } = data;
      let needsUpdate = false;
      let updatedAnalysis = { ...analysis };

      // Check and ensure all arrays exist
      if (!Array.isArray(analysis.strengths)) {
        updatedAnalysis.strengths = [];
        needsUpdate = true;
      }
      
      if (!Array.isArray(analysis.weaknesses)) {
        updatedAnalysis.weaknesses = [];
        needsUpdate = true;
      }
      
      if (!Array.isArray(analysis.priority_actions)) {
        updatedAnalysis.priority_actions = [];
        needsUpdate = true;
      }
      
      if (!Array.isArray(analysis.compliance_alerts)) {
        updatedAnalysis.compliance_alerts = [];
        needsUpdate = true;
      }

      // Check metrics exist
      if (!analysis.metrics) {
        updatedAnalysis.metrics = {
          title_score: 0,
          images_score: 0,
          description_score: 0,
          price_score: 0,
          bullet_points_score: 0,
          reviews_score: 0
        };
        needsUpdate = true;
      }

      // Update if needed
      if (needsUpdate) {
        setData(prev => {
          if (!prev) return null;
          return {
            ...prev,
            analysis: updatedAnalysis
          } as RoastData;
        });
      }
    }
  }, [data]);

  logger.log('Rendering with strengths:', analysis?.strengths);
  logger.log('Rendering with weaknesses:', analysis?.weaknesses);
  logger.log('Rendering with priority_actions:', analysis?.priority_actions);
  logger.log('Rendering with metrics:', analysis?.metrics);

  // Helper to safely render arrays
  const safelyRenderArray = (array: any[] | null | undefined) => {
    if (!array || !Array.isArray(array) || array.length === 0) {
      return (
        <li style={{ marginBottom: '10px', paddingLeft: '5px' }}>
          No data available
        </li>
      );
    }

    return array.map((item: string, index: number) => (
      <li key={index} style={{ marginBottom: '10px', paddingLeft: '5px' }}>
        {item}
      </li>
    ));
  };

  // Removed the problematic useEffect that's causing infinite loops
  /* React.useEffect(() => {
    const parseDataForDisplay = (data: any) => {
      // This code is removed to prevent the infinite loop
    };
    
    if (data) {
      const processedData = parseDataForDisplay({...data});
      if (processedData) {
        setData(processedData);
      }
    }
  }, [data]); */

  React.useEffect(() => {
    // Add global click handler to close dropdowns when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      // Only close if the click is outside of any dropdown
      const dropdowns = document.querySelectorAll('.dropdown-content');
      let clickedInside = false;
      
      dropdowns.forEach(dropdown => {
        if (dropdown.contains(event.target as Node)) {
          clickedInside = true;
        }
      });
      
      if (!clickedInside) {
        // setIsTopShareOpen(false);
        // setIsMetricsShareOpen(false);
      }
    };

    // Add scroll handler to update dropdown positions
    const handleScroll = () => {
      // if (isTopShareOpen && topShareButtonRef.current) {
      //   const rect = topShareButtonRef.current.getBoundingClientRect();
      //   setTopSharePosition({
      //     top: rect.bottom + 8,
      //     right: window.innerWidth - rect.right
      //   });
      // }
      
      // if (isMetricsShareOpen && metricsShareButtonRef.current) {
      //   const rect = metricsShareButtonRef.current.getBoundingClientRect();
      //   setMetricsSharePosition({
      //     top: rect.bottom + 8,
      //     left: rect.left + rect.width / 2
      //   });
      // }
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (loading) {
    return (
      <HelmetProvider>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h2>Loading analysis...</h2>
            <div className="loading-animation" style={{
              display: 'inline-block',
              width: '50px',
              height: '50px',
              border: '5px solid #f3f4f6',
              borderTopColor: '#4285F4',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
            <style>
              {`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        </div>
      </HelmetProvider>
    );
  }

  if (error) {
    return (
      <HelmetProvider>
        <Helmet>
          <title>Error - RoastMyListing.ai</title>
        </Helmet>
        <PromoBanner />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 'calc(100vh - 150px)',
          backgroundColor: '#f9fafc',
          padding: '20px'
        }}>
          <div style={{ 
            textAlign: 'center', 
            maxWidth: '600px', 
            padding: '30px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ color: '#ef4444', marginBottom: '20px', fontSize: '24px' }}>
              Failed to load roast data
            </h2>
            <p style={{ marginBottom: '20px', color: '#4b5563', lineHeight: '1.6' }}>
              {error}
            </p>
            <button 
              onClick={() => navigate('/')}
              style={{
                backgroundColor: '#f97316',
                color: 'white',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                boxShadow: '0 4px 6px rgba(249, 115, 22, 0.25)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(249, 115, 22, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(249, 115, 22, 0.25)';
              }}
            >
              Try a New Analysis
            </button>
          </div>
        </div>
      </HelmetProvider>
    );
  }

  if (!data) {
    return (
      <HelmetProvider>
        <Helmet>
          <title>No Data - RoastMyListing.ai</title>
        </Helmet>
        <PromoBanner />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 'calc(100vh - 150px)',
          backgroundColor: '#f9fafb',
          padding: '20px'
        }}>
          <div style={{ 
            textAlign: 'center', 
            maxWidth: '600px', 
            padding: '30px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ color: '#4b5563', marginBottom: '20px', fontSize: '24px' }}>
              No roast data available
            </h2>
            <p style={{ marginBottom: '20px', color: '#4b5563', lineHeight: '1.6' }}>
              Please try searching for the product again.
            </p>
            <button 
              onClick={() => navigate('/')}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                boxShadow: '0 4px 6px rgba(59, 130, 246, 0.25)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(59, 130, 246, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.25)';
              }}
            >
              Go to Search
            </button>
          </div>
        </div>
      </HelmetProvider>
    );
  }

  // Safely extract values from analysis with default empty arrays for collections
  const {
    roast_level = 'medium',
    roast_text = '',
    score = 0,
    strengths = [],
    weaknesses = [],
    metrics = {
      title_score: 0,
      images_score: 0,
      description_score: 0,
      price_score: 0,
      bullet_points_score: 0,
      reviews_score: 0
    },
    priority_actions = [],
    competitive_insight = '',
    compliance_alerts = []
  } = analysis || {};

  // Function to get score color
  const getScoreColor = (value: number) => {
    if (value >= 80) return '#22c55e';
    if (value >= 60) return '#f97316';
    return '#ef4444';
  };

  // Function to get roast level color
  const getRoastLevelColor = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'mild': return '#60a5fa'; // Light blue
      case 'medium': return '#f97316'; // Orange
      case 'spicy': return '#ef4444'; // Red
      case 'nuclear': return '#7c2d12'; // Dark brown
      default: return '#f97316';
    }
  };

  // Function to get roast level icon
  const getRoastLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'mild':
        return <span role="img" aria-label="snowflake">❄️</span>;
      case 'medium':
        return <span role="img" aria-label="fire">🔥</span>;
      case 'spicy':
        return <span role="img" aria-label="hot pepper">🌶️</span>;
      case 'nuclear':
        return <span role="img" aria-label="radiation">☢️</span>;
      default:
        return <span role="img" aria-label="fire">🔥</span>;
    }
  };

  // Get score color
  const scoreColor = getScoreColor(score);

  return (
    <HelmetProvider>
        <Helmet>
          <title>RoastMyListing.ai - {product_name}</title>
          <style>
            {`
              body {
                background-color: #f8fafc;
                margin: 0;
                padding: 0;
              }
              
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(-10px); }
                to { opacity: 1; transform: translateY(0); }
              }
              @keyframes fadeOut {
                from { opacity: 1; }
                to { opacity: 0; }
              }
              
              @keyframes flicker {
                0% { opacity: 0.8; height: 30px; }
                100% { opacity: 1; height: 35px; }
              }
              
              @keyframes shine {
                0% { left: -100%; }
                20% { left: 100%; }
                100% { left: 100%; }
              }
              .animate-in {
                animation: fadeIn 0.5s ease-out forwards;
              }
              
              .card {
                background-color: white;
                border-radius: 12px;
                box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
                transition: transform 0.3s, box-shadow 0.3s;
                position: relative;
                z-index: 0;
              }
              
              .card:hover {
                transform: translateY(-5px);
                box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
              }
              
              /* Global CSS to ensure dropdowns are always on top */
              .dropdown-content {
                position: fixed !important;
                z-index: 2147483647 !important; 
                pointer-events: auto !important;
              }
              
              /* Fix for elements with higher z-index */
              .card, .card:hover {
                transform: translateY(-5px);
                box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
                z-index: 0 !important;
                isolation: isolate;
              }
            `}
          </style>
        </Helmet>
        
        {/* Use the PromoBanner component from the homepage */}
        <PromoBanner />
        
        {/* Countdown Timer */}
        {showCountdown && (
          <RoastCountdown 
            roastLevel={roast_level} 
            createdAt={createdAt} 
            onClose={handleCloseCountdown} 
            onLoadingAnyway={handleLoadAnyway}
          />
        )}
        
        {/* Minimal blue background for logo */}
        <div style={{ 
          width: '100%', 
          backgroundColor: '#3b82f6',
          background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
          padding: '8px 0', 
          marginBottom: '2px',
          boxShadow: '0 1px 4px rgba(66, 133, 244, 0.2)'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 20px',
            gap: '4px' 
          }}>
            <div 
              onClick={() => navigate('/')}
              style={{ 
                cursor: 'pointer',
                display: 'inline-block',
                textAlign: 'center',
                marginBottom: '0'
              }}
            >
              <img 
                src="/logo.png" 
                alt="RoastMyListing.ai Logo"
                style={{ 
                  maxWidth: '450px', 
                  height: 'auto'
                }}
              />
            </div>
          </div>
        </div>
        
        {/* Main content */}
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '20px',
          position: 'relative'
        }}>
          {/* Loading state */}
          {loading && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '300px'
            }}>
              <div className="loading-spinner"></div>
            </div>
          )}
          
          <div style={{ 
            width: '100%', 
            maxWidth: '1200px', 
            margin: '0 auto', 
            padding: '0 20px 60px',
            animation: 'fadeIn 0.5s ease-out forwards',
            animationDelay: '0.2s',
            opacity: 0,
            position: 'relative',
            textAlign: 'left'
          }}>
            
            <div className="card" style={{ 
              padding: '30px',
              marginBottom: '30px',
              marginTop: '0'
            }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '15px' 
              }}>
                {/* Product Title - Moved back up */}
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <h1 style={{ 
                    fontSize: '24px', 
                    marginBottom: '10px', 
                    color: '#1e293b',
                    fontWeight: 'bold'
                  }}>{product_name}</h1>
                </div>
                
                {/* Product Image and Info Side by Side on larger screens */}
                <div style={{ 
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'center',
                  alignItems: isMobile ? 'center' : 'flex-start',
                  width: '100%',
                  gap: '20px',
                  marginBottom: '10px'
                }}>
                  {/* Product Image */}
                  <div style={{
                    padding: '12px',
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
                    maxWidth: isMobile ? '400px' : '300px',
                    maxHeight: isMobile ? '400px' : '300px',
                    overflow: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: isMobile ? 'auto' : '0 0 300px'
                  }}>
                    <img 
                      src={product_image} 
                      alt={product_name}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                    />
                  </div>

                  {/* Product Info (Price, Rating, Score, Roast Level, Buy Button) */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px',
                    flex: isMobile ? 'auto' : '1',
                    alignItems: 'flex-start',
                    width: '100%'
                  }}>
                    {/* Price and Rating */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '5px',
                      marginBottom: '15px',
                      width: '100%'
                    }}>
                      {price ? (
                        <div style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#B12704'
                        }}>
                          {price}
                        </div>
                      ) : null}
                      
                      {rating ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {[...Array(5)].map((_, i) => (
                              <span key={i} style={{
                                color: i < Math.floor(rating) ? '#FFA41C' : '#E7E7E7',
                                fontSize: '20px'
                              }}>★</span>
                            ))}
                          </div>
                          {review_count ? (
                            <span style={{ color: '#007185', fontSize: '14px' }}>
                              ({review_count.toLocaleString()} reviews)
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    
                    {/* Score, Roast Level, and Buy Button in one row */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '15px',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      width: '100%',
                      flexWrap: isMobile ? 'wrap' : 'nowrap',
                      marginBottom: '20px'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '10px 15px',
                        backgroundColor: scoreColor,
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        boxShadow: `0 4px 12px ${scoreColor}40`,
                        flex: '1',
                        minWidth: '100px',
                        maxWidth: isMobile ? '100%' : '150px',
                        height: '80px',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex: 0
                      }}>
                        <span style={{ fontSize: '12px', marginBottom: '2px' }}>SCORE</span>
                        <span style={{ fontSize: '22px' }}>{score}<span style={{ fontSize: '14px' }}>/100</span></span>
                      </div>
                      
                      {/* Roast Level with dynamic styling based on level */}
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '10px 15px',
                        backgroundColor: getRoastLevelColor(roast_level),
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        boxShadow: `0 4px 12px ${getRoastLevelColor(roast_level)}40`,
                        flex: '1',
                        minWidth: '100px',
                        maxWidth: isMobile ? '100%' : '150px',
                        height: '80px',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex: 0
                      }}>
                        <span style={{ fontSize: '12px', marginBottom: '2px' }}>ROAST LEVEL</span>
                        <span style={{ fontSize: '18px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {getRoastLevelIcon(roast_level)} {roast_level}
                        </span>
                      </div>
                      
                      {/* Buy on Amazon Button */}
                      {asin && (
                        <a 
                          href={`https://www.${getAmazonDomain(marketplace)}/dp/${asin}?tag=${AMAZON_AFFILIATE_TAG}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#FF9900',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0 20px',
                            height: '80px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            gap: '12px',
                            textDecoration: 'none',
                            boxShadow: '0 4px 12px rgba(255, 153, 0, 0.3), 0 0 20px rgba(255, 153, 0, 0.2)',
                            position: 'relative',
                            overflow: 'hidden',
                            flex: '1',
                            minWidth: isMobile ? '100%' : '180px',
                            maxWidth: isMobile ? '100%' : '280px'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#F08804';
                            e.currentTarget.style.transform = 'translateY(-3px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 153, 0, 0.4), 0 0 30px rgba(255, 153, 0, 0.3)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#FF9900';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 153, 0, 0.3), 0 0 20px rgba(255, 153, 0, 0.2)';
                          }}
                        >
                          {/* Flames effect */}
                          <div style={{
                            position: 'absolute',
                            bottom: '-10px',
                            left: '0',
                            width: '100%',
                            height: '30px',
                            background: 'linear-gradient(0deg, rgba(255,0,0,0.3) 0%, rgba(255,153,0,0.3) 50%, rgba(255,255,0,0.1) 100%)',
                            clipPath: 'polygon(0% 100%, 5% 80%, 10% 100%, 15% 70%, 20% 100%, 25% 85%, 30% 100%, 35% 80%, 40% 100%, 45% 80%, 50% 100%, 55% 75%, 60% 100%, 65% 85%, 70% 100%, 75% 80%, 80% 100%, 85% 90%, 90% 100%, 95% 75%, 100% 100%)',
                            animation: 'flicker 0.5s ease-in-out infinite alternate',
                            zIndex: 1
                          }} />
                          
                          {/* Shine effect */}
                          <div style={{
                            position: 'absolute',
                            top: '-100%',
                            left: '-100%',
                            width: '300%',
                            height: '300%',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            transform: 'rotate(30deg)',
                            animation: 'shine 3s ease-in-out infinite',
                            pointerEvents: 'none',
                            zIndex: 2
                          }} />
                          
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style={{ width: '32px', height: '32px', fill: 'currentColor' }}>
                            <path d="M257.2 162.7c-48.7 1.8-169.5 15.5-169.5 117.5 0 109.5 138.3 114 183.5 43.2 6.5 10.2 35.4 37.5 45.3 46.8l56.8-56S341 288.9 341 261.4V114.3C341 89 316.5 32 228.7 32 140.7 32 94 87 94 136.3l73.5 6.8c16.3-49.5 54.2-49.5 54.2-49.5 40.7-.1 35.5 29.8 35.5 69.1zm0 86.8c0 80-84.2 68-84.2 17.2 0-47.2 50.5-56.7 84.2-57.8v40.6zm136 163.5c-7.7 10-70 67-174.5 67S34.2 408.5 9.7 379c-6.8-7.7 1-11.3 5.5-8.3C88.5 415.2 203 488.5 387.7 401c7.5-3.7 13.3 2 5.5 12zm39.8 2.2c-6.5 15.8-16 26.8-21.2 31-5.5 4.5-9.5 2.7-6.5-3.8s19.3-46.5 12.7-55c-6.5-8.3-37-4.3-48-3.2-10.8 1-13 2-14-.3-2.3-5.7 21.7-15.5 37.5-17.5 15.7-1.8 41-.8 46 5.7 3.7 5.1 0 27.1-6.5 43.1z"/>
                          </svg>
                          <span>Buy on Amazon</span>
                        </a>
                      )}
                    </div>
                    
                    {/* Like/Dislike Buttons */}
                    <div style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      backgroundColor: 'white',
                      padding: '20px',
                      borderRadius: '12px',
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                      marginBottom: '25px',
                      width: '100%'
                    }}>
                      <div style={{ 
                        fontSize: '22px',
                        fontWeight: 'bold',
                        color: '#1f2937',
                        textAlign: 'left',
                        marginBottom: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span role="img" aria-label="thumbs up down">👍👎</span> Rate & Spread the Roast
                      </div>
                      
                      <div style={{ 
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}>
                        {/* Like Button */}
                        <button 
                          onClick={() => handleVote('like')}
                          className={`viral-button viral-button-like ${
                            userVote === 'like' ? 'viral-button-active' : ''
                          }`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: userVote === 'like' ? '#2563eb' : '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 15px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            gap: '6px'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#2563eb';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = userVote === 'like' ? '#2563eb' : '#3b82f6';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {userVote === 'like' ? (
                            <FaThumbsUp size={16} />
                          ) : (
                            <FaRegThumbsUp size={16} />
                          )}
                          <span>{likes}</span>
                        </button>
                        
                        {/* Dislike Button */}
                        <button 
                          onClick={() => handleVote('dislike')}
                          className={`viral-button viral-button-dislike ${
                            userVote === 'dislike' ? 'viral-button-active' : ''
                          }`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: userVote === 'dislike' ? '#dc2626' : '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 15px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            gap: '6px'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc2626';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = userVote === 'dislike' ? '#dc2626' : '#ef4444';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {userVote === 'dislike' ? (
                            <FaThumbsDown size={16} />
                          ) : (
                            <FaRegThumbsDown size={16} />
                          )}
                          <span>{dislikes}</span>
                        </button>
                        
                        {/* Share Button */}
                        <ShareButtons 
                          shares={shares} 
                          onShare={(platform) => {
                            // Pass the share text and product info to ShareButtons
                            const shareText = data?.product_name 
                              ? `🚨 AI ROAST ALERT: This listing just got absolutely DESTROYED with a ${data?.analysis?.score}/100! Even the algorithm is laughing! 🔥 #ListingHomicide` 
                              : `🚨 SAVAGE: AI just committed a murder on Amazon! This roast is so brutal it needs a warning label! 🔥 #ListingHomicide`;
                            
                            handleShare(platform, shareText);
                            recordShare(asin ?? '', platform);
                          }} 
                          copySuccess={copySuccess} 
                          notification={notification}
                          productName={data?.product_name}
                          score={data?.analysis?.score || 0}
                          asin={asin}
                          productImage={data?.product_image}
                        />
                        
                        {/* Generate New Roast Button */}
                        <Link to="/" style={{ textDecoration: 'none', marginLeft: '10px' }}>
                          <button
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f97316',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 15px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              gap: '6px',
                              height: '34px'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#ea580c';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = '#f97316';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <FaFire size={16} />
                            <span>New Roast</span>
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Roast Results */}
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
              marginBottom: '30px',
              animation: 'fadeIn 0.5s ease-out forwards',
              animationDelay: '0.2s',
              opacity: 0,
              width: '100%',
              textAlign: 'left'
            }}>
              <h2 style={{ 
                marginBottom: '15px', 
                color: '#1e293b',
                fontSize: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span role="img" aria-label="fire">🔥</span> The Roast
              </h2>
              <p style={{ 
                fontSize: '16px',
                lineHeight: '1.8',
                color: '#334155',
                textAlign: 'left'
              }}>{roast_text}</p>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
              gap: '25px', 
              marginTop: '30px',
              width: '100%'
            }}>
              <div className="card" style={{ padding: '25px' }}>
                <h3 style={{ 
                  marginBottom: '15px', 
                  color: '#22c55e',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textAlign: 'left'
                }}>
                  <span role="img" aria-label="checkmark">✅</span> Product Strengths
                </h3>
                <ul style={{ 
                  paddingLeft: '25px',
                  color: '#334155',
                  lineHeight: '1.6',
                  textAlign: 'left'
                }}>
                  {safelyRenderArray(analysis?.strengths || ['Listing has good optimization potential'])}
                </ul>
              </div>
              
              <div className="card" style={{ padding: '25px' }}>
                <h3 style={{ 
                  marginBottom: '15px', 
                  color: '#ef4444',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textAlign: 'left'
                }}>
                  <span role="img" aria-label="warning">⚠️</span> Product Weaknesses
                </h3>
                <ul style={{ 
                  paddingLeft: '25px',
                  color: '#334155',
                  lineHeight: '1.6',
                  textAlign: 'left'
                }}>
                  {safelyRenderArray(analysis?.weaknesses || ['Listing needs improvement in several areas'])}
                </ul>
              </div>
            </div>

            <div className="card" style={{ 
              marginTop: '30px',
              padding: '25px',
              width: '100%'
            }}>
              <h3 style={{ 
                marginBottom: '15px', 
                color: '#3b82f6',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                textAlign: 'left'
              }}>
                <span role="img" aria-label="rocket">🚀</span> Priority Actions
              </h3>
              {(() => {
                console.log("Rendering priority actions section");
                
                // Extract data from analysis object and provide defaults
                const analysis = data?.analysis;
                console.log("FULL ANALYSIS OBJECT:", JSON.stringify(analysis, null, 2));
                
                // First try to get priorityActions from the database improvements field
                let priorityActions: string[] = [];
                
                if (analysis) {
                  // Try database improvements field as our source of priority actions
                  const databaseImprovements = data.improvements;
                  console.log("Database improvements field:", databaseImprovements);
                  
                  if (databaseImprovements) {
                    // Handle string or array format
                    if (typeof databaseImprovements === 'string') {
                      try {
                        const parsed = JSON.parse(databaseImprovements);
                        priorityActions = Array.isArray(parsed) ? parsed : [databaseImprovements];
                      } catch (e) {
                        priorityActions = [databaseImprovements];
                      }
                    } else if (Array.isArray(databaseImprovements)) {
                      priorityActions = databaseImprovements;
                    }
                  }
                  
                  // If we still don't have any actions, try using priority_actions directly
                  if (priorityActions.length === 0 && analysis.priority_actions) {
                    if (Array.isArray(analysis.priority_actions)) {
                      priorityActions = analysis.priority_actions;
                    } else if (typeof analysis.priority_actions === 'string') {
                      try {
                        const parsed = JSON.parse(analysis.priority_actions);
                        priorityActions = Array.isArray(parsed) ? parsed : [analysis.priority_actions];
                      } catch (e) {
                        priorityActions = [analysis.priority_actions];
                      }
                    }
                  }
                  
                  // If we still don't have any actions, try using analysis.improvements
                  if (priorityActions.length === 0 && analysis.improvements) {
                    if (Array.isArray(analysis.improvements)) {
                      priorityActions = analysis.improvements;
                    } else if (typeof analysis.improvements === 'string') {
                      try {
                        const parsed = JSON.parse(analysis.improvements);
                        priorityActions = Array.isArray(parsed) ? parsed : [analysis.improvements];
                      } catch (e) {
                        priorityActions = [analysis.improvements];
                      }
                    }
                  }
                }
                
                // If we still have no actions, provide defaults based on data
                if (priorityActions.length === 0 && analysis) {
                  // Create default action items based on low scores
                  if (analysis.metrics) {
                    const metrics = analysis.metrics;
                    let defaultActions = [];
                    
                    if (metrics.description_score < 50) {
                      defaultActions.push("Improve the product description with more detailed information");
                    }
                    
                    if (metrics.images_score < 50) {
                      defaultActions.push("Add more high-quality product images showing different angles and features");
                    }
                    
                    if (metrics.title_score < 50) {
                      defaultActions.push("Optimize the product title with relevant keywords and benefits");
                    }
                    
                    if (metrics.bullet_points_score < 50) {
                      defaultActions.push("Enhance bullet points to highlight key features and benefits");
                    }
                    
                    if (defaultActions.length > 0) {
                      priorityActions = defaultActions;
                    } else {
                      priorityActions = ["Optimize product listing to improve visibility and conversion rate"];
                    }
                  } else {
                    priorityActions = ["Optimize product listing to improve visibility and conversion rate"];
                  }
                }
                
                // Additional debugging
                logger.log("Final priority actions array:", priorityActions);
                logger.log("Is array:", Array.isArray(priorityActions));
                logger.log("Length:", priorityActions.length);
                
                // Always ensure priorities is a string array
                priorityActions = priorityActions.map(action => String(action));
                
                // Check for valid array with items
                if (priorityActions.length > 0) {
                  return (
                    <ol style={{ 
                      paddingLeft: '25px',
                      color: '#334155',
                      lineHeight: '1.6',
                      textAlign: 'left'
                    }}>
                      {priorityActions.map((action, index) => (
                        <li key={index} style={{ marginBottom: '10px', paddingLeft: '5px' }}>
                          {action}
                        </li>
                      ))}
                    </ol>
                  );
                } else {
                  return <p style={{ 
                    fontSize: '16px',
                    lineHeight: '1.8',
                    color: '#334155',
                    textAlign: 'left'
                  }}>
                    No priority actions available.
                  </p>;
                }
              })()}
            </div>

            <div className="card" style={{ 
              marginTop: '30px',
              padding: '25px',
              width: '100%'
            }}>
              <h3 style={{ 
                marginBottom: '20px', 
                color: '#1e293b',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                textAlign: 'left'
              }}>
                <span role="img" aria-label="chart">📊</span> Performance Metrics
              </h3>
              
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr',
                gap: '20px'
              }}>
                {metrics && typeof metrics === 'object' ? 
                  Object.entries(metrics || {}).map(([key, value]) => {
                    const metricValue = Number(value);
                    const metricColor = getScoreColor(metricValue);
                    
                    return (
                      <div key={key} style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '500',
                          color: '#64748b'
                        }}>
                          {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </div>
                        <div style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '50%',
                          backgroundColor: '#f8fafc',
                          border: `3px solid ${metricColor}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '22px',
                          fontWeight: 'bold',
                          color: metricColor
                        }}>
                          {String(value)}
                        </div>
                      </div>
                    );
                  })
                : 
                  <div style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '20px', color: '#64748b' }}>
                    No metrics data available
                  </div>
                }
              </div>
            </div>

            {/* Compliance Alerts Section - Only show if there are alerts */}
            {Array.isArray(compliance_alerts) && compliance_alerts.length > 0 && (
              <div className="card" style={{ 
                marginTop: '30px',
                padding: '25px',
                width: '100%',
                border: '1px solid #fca5a5',
                backgroundColor: '#fee2e2'
              }}>
                <h3 style={{ 
                  marginBottom: '15px', 
                  color: '#dc2626',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textAlign: 'left'
                }}>
                  <span role="img" aria-label="warning">⚠️</span> Compliance Alerts
                </h3>
                <ul style={{ 
                  paddingLeft: '25px',
                  color: '#7f1d1d',
                  lineHeight: '1.6',
                  textAlign: 'left'
                }}>
                  {safelyRenderArray(compliance_alerts)}
                </ul>
              </div>
            )}
          </div>
        </div>
      </HelmetProvider>
    );
};

export default Results;
